import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  MessageSquare, Send, Loader2, Sparkles, Clock,
  ChevronRight, CheckCircle, AlertCircle, Database
} from 'lucide-react';

export default function AskMolecuAIPage() {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    loadInitial();
  }, []);

  const loadInitial = async () => {
    try {
      const [h, s] = await Promise.all([
        api.get('/nl-query/history'),
        api.get('/nl-query/suggestions'),
      ]);
      setHistory(h.data);
      setSuggestions(s.data);
    } catch (e) { console.error(e); }
    finally { setInitLoading(false); }
  };

  const handleAsk = async (q) => {
    const text = q || question;
    if (!text.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/nl-query/ask', { question: text });
      setResult(data);
      setQuestion('');
      const { data: h } = await api.get('/nl-query/history');
      setHistory(h);
    } catch (e) {
      setResult({ success: false, error: e.response?.data?.detail || 'Query failed' });
    }
    finally { setLoading(false); }
  };

  const columns = result?.data?.[0] ? Object.keys(result.data[0]) : [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="ask-molecuai-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Ask MolecuAI</h1>
        <p className="text-sm text-slate-500 mt-1">Natural language queries powered by Claude AI</p>
      </div>

      {/* Query Input */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0A2463]/40" />
              <Input
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                placeholder="Ask a question about your data..."
                className="rounded-sm pl-9 h-11 text-sm"
                data-testid="nl-query-input"
              />
            </div>
            <Button
              onClick={() => handleAsk()}
              disabled={loading || !question.trim()}
              data-testid="nl-query-submit"
              className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white h-11 px-5"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>

          {/* Suggestions */}
          {!result && suggestions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setQuestion(s); handleAsk(s); }}
                  data-testid={`suggestion-${i}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-sm text-[11px] text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                >
                  <ChevronRight className="h-3 w-3 text-slate-400" />
                  {s}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-[#0A2463]" />
              <div>
                <p className="text-sm font-medium text-slate-700">Analyzing your question...</p>
                <p className="text-xs text-slate-400 mt-0.5">Translating to SQL and querying the database</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4">
          {/* Answer Card */}
          <Card className={`bg-white border rounded-sm shadow-none ${result.success ? 'border-slate-200' : 'border-red-200'}`} data-testid="nl-query-result">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                {result.success ? (
                  <div className="w-8 h-8 rounded-sm bg-[#0A2463]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="h-4 w-4 text-[#0A2463]" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-sm bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {result.success ? (
                    <>
                      <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="nl-query-summary">{result.summary}</p>
                      <div className="flex gap-3 mt-3 text-[10px] text-slate-400">
                        <span>{result.row_count} rows</span>
                        <span>{result.execution_time_ms}ms</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-red-700">{result.error}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SQL */}
          {result.success && result.generated_sql && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
                  <Database className="h-3 w-3" />Generated SQL
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <pre className="text-[11px] font-mono text-slate-600 bg-slate-50 p-3 rounded-sm overflow-x-auto border border-slate-100" data-testid="nl-query-sql">
                  {result.generated_sql}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Data Table */}
          {result.success && result.data && result.data.length > 0 && (
            <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800">
                  Results ({result.row_count} rows)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="nl-query-data-table">
                    <thead>
                      <tr className="border-b border-slate-100">
                        {columns.map(col => (
                          <th key={col} className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-2 whitespace-nowrap">
                            {col.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.slice(0, 20).map((row, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                          {columns.map(col => (
                            <td key={col} className="px-4 py-2 text-xs font-mono text-slate-700 whitespace-nowrap max-w-[200px] truncate">
                              {row[col] != null ? String(row[col]) : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {result.data.length > 20 && (
                  <p className="text-center text-xs text-slate-400 py-2">Showing 20 of {result.row_count} rows</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" />Query History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-50">
              {history.slice(0, 15).map(h => (
                <div
                  key={h.id}
                  className="flex items-start gap-3 px-5 py-3 hover:bg-slate-50/50 cursor-pointer transition-colors"
                  onClick={() => { setQuestion(h.question); inputRef.current?.focus(); }}
                  data-testid={`history-item-${h.id}`}
                >
                  <div className="mt-0.5">
                    {h.success ? (
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{h.question}</p>
                    {h.result_summary && (
                      <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-1">{h.result_summary}</p>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-slate-300 shrink-0">
                    {h.execution_time_ms}ms
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
